import { useQuery } from "@tanstack/react-query";
import React, { useEffect } from "react";
import { withNamespaces } from "react-i18next";
import { toast } from "react-toastify";
import SupplierProductService from "src/modules/3rd-party-management/apis/common/SupplierProductService";
import {
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Row,
    Col
} from "reactstrap";
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";
import DateUtils from "src/services/utils/DateUtils";
import {
    INTERNATIONAL_DATE_FORMAT
} from 'src/common/constants';
import GeoMap from "src/modules/3rd-party-management/components/geo-map";

const ProductQuickViewModal = ({ t, isOpen, toggle, productId }) => {
    const dateUtils = new DateUtils();

    const {
        isFetching: fetchingInprocess,
        data: productDetails,
        refetch: fetchProductDetails
    } = useQuery({
        queryKey: [
            '3rd-party-management-fetch-supplier-product-detail',
            productId
        ],
        queryFn: async () => {
            const service = SupplierProductService.getInstance();

            return await service.show(productId, {});
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: false,
        onError: () => {
            toast(t('An error occurred while fetching product details.'), {
                type: 'error'
            });
        }
    });

    useEffect(() => {
        if(isOpen) {
            fetchProductDetails()
        }
    }, [
        isOpen,
        fetchProductDetails
    ]);

    const isEudrEnabled = isFeatureEnabledForActiveModule('eudr_activation');
    const hasEnitityFeature = isFeatureEnabledForActiveModule('entity_management');

    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
            role="dialog"
            aria-modal="true"
            aria-labelledby="productQuickViewTitle"
            aria-describedby="productQuickViewDesc"
            size="xl"
        >
            <ModalHeader toggle={toggle} id="productQuickViewTitle">
                {t('Product Quick View')}
            </ModalHeader>

            {/* Hidden description for accessibility */}
            <p id="productQuickViewDesc" className="visually-hidden">
                {t('Quick view of product details.')}
            </p>

            <ModalBody>
                <Row>
                    <Col sm='12' md='6' className="mb-3">
                        <strong>{t('NACE Code')}:</strong><br/>
                        {fetchingInprocess ? (
                            <div className="dt-field dt-skeleton mt-1" style={{width: '50%'}} />
                        ) : productDetails?.name?.name}
                    </Col>

                    <Col sm='12' md='6' className="mb-3">
                        <strong>{t('Product Name')}:</strong><br/>
                        {fetchingInprocess ? (
                            <div className="dt-field dt-skeleton mt-1" style={{width: '50%'}} />
                        ) : productDetails?.productName}
                    </Col>

                    {hasEnitityFeature && (
                        <Col sm='12' className="mb-3">
                            <strong>{t('Legal Entities')}:</strong><br/>
                            {fetchingInprocess ? (
                                <div className="dt-field dt-skeleton mt-1" style={{width: '50%'}} />
                            ) : (
                                <div>
                                    {(productDetails?.legal_entities || []).map((legal) => {
                                        return (
                                            <p className="mb-1">
                                                {t(legal.name)}
                                            </p>
                                        )
                                    })}
                                </div>
                            )}
                        </Col>
                    )}
                </Row>
                <Row>
                    {isEudrEnabled && (
                        <>
                            <Col sm='12' md='6' className="mb-3">
                                <strong>{t('Lot Number')}:</strong><br />
                                {fetchingInprocess ? (
                                    <div className="dt-field dt-skeleton mt-1" style={{ width: '100%' }} />
                                ) : productDetails?.lotNumber}
                            </Col>
                            <Col sm='12' md='6' className="mb-3">
                                <strong>{t('Internal Product ID')}:</strong><br />
                                {fetchingInprocess ? (
                                    <div className="dt-field dt-skeleton mt-1" style={{ width: '100%' }} />
                                ) : productDetails?.internalProductId}
                            </Col>

                            <Col sm='12' md='6' className="mb-3">
                                <strong>{t('Date of Import')}:</strong><br />
                                {fetchingInprocess ? (
                                    <div className="dt-field dt-skeleton mt-1" style={{ width: '100%' }} />
                                ) : dateUtils.convertDateToDate(
                                    productDetails?.importDate, 
                                    INTERNATIONAL_DATE_FORMAT
                                )}
                            </Col>
                            <Col sm='12' md='6' className="mb-3">
                                <strong>{t('Import Country')}:</strong><br />
                                {fetchingInprocess ? (
                                    <div className="dt-field dt-skeleton mt-1" style={{ width: '100%' }} />
                                ) : t(productDetails?.importFromCountry?.name)}
                            </Col>

                            <Col sm='12' md='6' className="mb-3">
                                <strong>{t('Date of Export')}:</strong><br />
                                {fetchingInprocess ? (
                                    <div className="dt-field dt-skeleton mt-1" style={{ width: '100%' }} />
                                ) : dateUtils.convertDateToDate(
                                    productDetails?.exportDate, 
                                    INTERNATIONAL_DATE_FORMAT
                                )}
                            </Col>
                            <Col sm='12' md='6' className="mb-3">
                                <strong>{t('Export Country')}:</strong><br />
                                {fetchingInprocess ? (
                                    <div className="dt-field dt-skeleton mt-1" style={{ width: '100%' }} />
                                ) : t(productDetails?.exportToCountry?.name)}
                            </Col>

                            <Col sm='12' md='6' className="mb-3">
                                <strong>{t('Quantity (Metric Tonnes)')}:</strong><br />
                                {fetchingInprocess ? (
                                    <div className="dt-field dt-skeleton mt-1" style={{ width: '100%' }} />
                                ) : productDetails?.metricTonnes}
                            </Col>
                            <Col sm='12' md='6' className="mb-3">
                                <strong>{t('Country of Origin')}:</strong><br />
                                {fetchingInprocess ? (
                                    <div className="dt-field dt-skeleton mt-1" style={{ width: '100%' }} />
                                ) : t(productDetails?.originCountry?.name)}
                            </Col>

                            <Col sm='12' md='6' className="mb-3">
                                <strong>{t('Container Number')}:</strong><br />
                                {fetchingInprocess ? (
                                    <div className="dt-field dt-skeleton mt-1" style={{ width: '100%' }} />
                                ) : productDetails?.containerNumber}
                            </Col>
                            <Col sm='12' md='6' className="mb-3">
                                <strong>{t('Shipment Number')}:</strong><br />
                                {fetchingInprocess ? (
                                    <div className="dt-field dt-skeleton mt-1" style={{ width: '100%' }} />
                                ) : t(productDetails?.shipmentNumber)}
                            </Col>

                            <Col sm='12' md='4' className="mb-3">
                                <strong>{t('DDS Reference Number')}:</strong><br />
                                {fetchingInprocess ? (
                                    <div className="dt-field dt-skeleton mt-1" style={{ width: '100%' }} />
                                ) : productDetails?.ddsReferenceNumber}
                            </Col>
                            <Col sm='12' md='4' className="mb-3">
                                <strong>{t('DDS Verification Number')}:</strong><br />
                                {fetchingInprocess ? (
                                    <div className="dt-field dt-skeleton mt-1" style={{ width: '100%' }} />
                                ) : productDetails?.ddsVerificationNumber}
                            </Col>
                            <Col sm='12' md='4' className="mb-3">
                                <strong>{t('DDS Statement Status')}:</strong><br />
                                {fetchingInprocess ? (
                                    <div className="dt-field dt-skeleton mt-1" style={{ width: '100%' }} />
                                ) : (
                                    productDetails?.ddsDeclarationType === 'provide_reference_numbers' ? (
                                        productDetails?.statementValidation === false ? (
                                            <span>
                                                <i 
                                                    className="ri-close-line me-1 text-danger align-middle font-size-18" 
                                                />
                                                {t('Invalid')}
                                            </span>
                                        ) : productDetails?.statementValidation === true ? (
                                            <span>
                                                <i 
                                                    className="ri-checkbox-fill me-1 text-success align-middle font-size-18" 
                                                />
                                                {t('Valid')}
                                            </span>
                                        ) : (
                                            <span>
                                                {t('Validating')}...
                                            </span>
                                        )
                                    ) : (
                                        '-'
                                    )
                                )}
                            </Col>

                            <Col sm='12' md='4' className="mb-3">
                                <strong>{t('HS Code (6-digit)')}:</strong><br />
                                {fetchingInprocess ? (
                                    <div className="dt-field dt-skeleton mt-1" style={{ width: '100%' }} />
                                ) : productDetails?.hsCode}
                            </Col>
                            <Col sm='12' md='4' className="mb-3">
                                <strong>{t('KN Code (8-digit)')}:</strong><br />
                                {fetchingInprocess ? (
                                    <div className="dt-field dt-skeleton mt-1" style={{ width: '100%' }} />
                                ) : productDetails?.knCode}
                            </Col>

                            <Col sm='12' md='4' className="mb-3">
                                <strong>{t('TARIC Code (10-digit)')}:</strong><br />
                                {fetchingInprocess ? (
                                    <div className="dt-field dt-skeleton mt-1" style={{ width: '100%' }} />
                                ) : productDetails?.taricCode}
                            </Col>
                        </>
                    )}

                    <Col sm='12' md={isEudrEnabled ? '6' : '12'} className="mb-3">
                        <strong>{t('Description')}:</strong><br/>
                        {fetchingInprocess ? (
                            <div className="dt-field dt-skeleton mt-1" style={{width: '100%'}} />
                        ) : productDetails?.description}
                    </Col>

                    {isEudrEnabled && (
                        <Col sm='12' className="mb-3">
                            <strong>{t('Geolocation')}:</strong><br/>

                            <GeoMap
                                readOnly={true}
                                preDefinedGeo={productDetails?.geolocationReference}
                                multiDraw={true}
                            />
                        </Col>
                    )}

                    {/* {isEudrEnabled && (
                        <Col sm='12'>
                            <strong>{t('Geolocation')}:</strong><br/>
                            {fetchingInprocess ? (
                                <div className="dt-field dt-skeleton mt-1" style={{width: '100%'}} />
                            ) : (
                                productDetails?.geolocationReference
                                ? `Polygon with ${productDetails.geolocationReference.geometry.coordinates?.[0]?.length} points`
                                : "Not provided"
                            )}
                        </Col>
                    )} */}
                </Row>
            </ModalBody>

            <ModalFooter>
                <Button color="secondary" onClick={toggle}>
                    {t('Close')}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default withNamespaces()(
    ProductQuickViewModal
);
